import React from 'react';
import { CheckCircle2, AlertTriangle, RefreshCw, Database, XCircle } from 'lucide-react';
import { TODAY } from '@/data/financial-systems';

const subMinutes = (date: Date, mins: number) => new Date(date.getTime() - mins * 60 * 1000);
const subHours = (date: Date, hrs: number) => new Date(date.getTime() - hrs * 60 * 60 * 1000);
const subDays = (date: Date, days: number) => new Date(date.getTime() - days * 24 * 60 * 60 * 1000);

const fmtTime = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const SOURCES = [
  {
    name: 'Access Financials (Primary)',
    type: 'ERP',
    status: 'healthy' as const,
    lastSync: subMinutes(TODAY, 12),
    recordsIn: 1_847,
    recordsUpdated: 23,
    recordsErrored: 0,
    syncDuration: '4.2s',
    nextSync: '15 mins',
    dataTypes: ['Bank Transactions', 'Invoices', 'Purchase Orders', 'Credit Notes'],
    connectionHealth: 99.8,
  },
  {
    name: 'Access Financials (Payroll)',
    type: 'ERP',
    status: 'healthy' as const,
    lastSync: subMinutes(TODAY, 47),
    recordsIn: 312,
    recordsUpdated: 8,
    recordsErrored: 0,
    syncDuration: '1.8s',
    nextSync: '13 mins',
    dataTypes: ['Payroll Runs', 'PAYE Submissions', 'Employee Records'],
    connectionHealth: 99.6,
  },
  {
    name: 'Dimensions Cloud',
    type: 'Dimensions',
    status: 'warning' as const,
    lastSync: subHours(TODAY, 2),
    recordsIn: 94,
    recordsUpdated: 2,
    recordsErrored: 3,
    syncDuration: '12.4s',
    nextSync: '58 mins',
    dataTypes: ['Project Codes', 'Cost Centres', 'Dimension Sets'],
    connectionHealth: 96.2,
  },
  {
    name: 'Excel Import',
    type: 'Manual',
    status: 'healthy' as const,
    lastSync: subDays(TODAY, 1),
    recordsIn: 156,
    recordsUpdated: 156,
    recordsErrored: 0,
    syncDuration: '0.3s',
    nextSync: 'Manual',
    dataTypes: ['Budget Data', 'Forecast Adjustments'],
    connectionHealth: 100,
  },
];

const SYNC_LOG = [
  { time: subMinutes(TODAY, 12), source: 'Access Financials (Primary)', event: 'Sync completed', detail: '23 records updated, 0 errors', type: 'success' as const },
  { time: subMinutes(TODAY, 47), source: 'Access Financials (Payroll)', event: 'Sync completed', detail: '8 records updated, 0 errors', type: 'success' as const },
  { time: subHours(TODAY, 2), source: 'Dimensions Cloud', event: 'Sync completed with warnings', detail: '3 records failed validation — dimension codes not found in mapping table', type: 'warning' as const },
  { time: subHours(TODAY, 2.2), source: 'Dimensions Cloud', event: 'Retry attempted', detail: 'Connection timeout on initial attempt — resolved on retry', type: 'warning' as const },
  { time: subDays(TODAY, 1), source: 'Excel Import', event: 'Manual import completed', detail: 'Budget data uploaded by Sarah Mitchell — 156 rows processed', type: 'success' as const },
  { time: subDays(TODAY, 1), source: 'Access Financials (Primary)', event: 'Sync completed', detail: '41 records updated, 0 errors', type: 'success' as const },
];

const statusConfig = {
  healthy: { label: 'Healthy', colour: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800', icon: <CheckCircle2 className="h-4 w-4 text-green-600" /> },
  warning: { label: 'Warning', colour: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800', icon: <AlertTriangle className="h-4 w-4 text-amber-600" /> },
  error: { label: 'Error', colour: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800', icon: <XCircle className="h-4 w-4 text-red-600" /> },
};

export const DataSourceSyncReportPreview: React.FC = () => {
  const reportDate = TODAY.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const totalRecords = SOURCES.reduce((s, r) => s + r.recordsIn, 0);
  const totalErrors = SOURCES.reduce((s, r) => s + r.recordsErrored, 0);
  const healthySources = SOURCES.filter(s => s.status === 'healthy').length;

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Data Source Sync Report</h3>
          <p className="text-sm text-muted-foreground">BuildRight Construction Ltd · All connected sources</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <Database className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{SOURCES.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Connected Sources</p>
        </div>
        <div className="rounded-lg border bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800 p-4 text-center">
          <CheckCircle2 className="h-5 w-5 mx-auto mb-2 text-green-600" />
          <p className="text-2xl font-bold text-green-700 dark:text-green-400">{healthySources}</p>
          <p className="text-xs text-muted-foreground mt-1">Sources Healthy</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <RefreshCw className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{totalRecords.toLocaleString('en-GB')}</p>
          <p className="text-xs text-muted-foreground mt-1">Records Processed Today</p>
        </div>
        <div className={`rounded-lg border p-4 text-center ${totalErrors > 0 ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800' : 'bg-muted/40'}`}>
          {totalErrors > 0
            ? <AlertTriangle className="h-5 w-5 mx-auto mb-2 text-amber-600" />
            : <CheckCircle2 className="h-5 w-5 mx-auto mb-2 text-green-600" />}
          <p className={`text-2xl font-bold ${totalErrors > 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}>{totalErrors}</p>
          <p className="text-xs text-muted-foreground mt-1">Validation Errors</p>
        </div>
      </div>

      {/* Source cards */}
      <div>
        <p className="text-sm font-semibold mb-3">Source Status</p>
        <div className="space-y-3">
          {SOURCES.map((source, i) => {
            const cfg = statusConfig[source.status];
            return (
              <div key={i} className={`rounded-lg border px-4 py-4 ${cfg.bg}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {cfg.icon}
                    <div>
                      <p className="font-semibold text-sm">{source.name}</p>
                      <p className="text-xs text-muted-foreground">{source.type} · Uptime {source.connectionHealth}%</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-xs font-semibold ${cfg.colour}`}>{cfg.label}</p>
                    <p className="text-xs text-muted-foreground">Next sync: {source.nextSync}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Last Sync</p>
                    <p className="text-xs font-medium">{fmtDate(source.lastSync)} {fmtTime(source.lastSync)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Records In</p>
                    <p className="text-xs font-medium">{source.recordsIn.toLocaleString('en-GB')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Updated</p>
                    <p className="text-xs font-medium">{source.recordsUpdated}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Errors</p>
                    <p className={`text-xs font-medium ${source.recordsErrored > 0 ? 'text-amber-600' : ''}`}>{source.recordsErrored}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {source.dataTypes.map(dt => (
                    <span key={dt} className="inline-flex items-center rounded-full bg-background/60 px-2 py-0.5 text-xs text-muted-foreground border">
                      {dt}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sync log */}
      <div>
        <p className="text-sm font-semibold mb-3">Recent Sync Log</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/60">
                <th className="px-4 py-3 text-left font-semibold">Time</th>
                <th className="px-4 py-3 text-left font-semibold">Source</th>
                <th className="px-4 py-3 text-left font-semibold">Event</th>
                <th className="px-4 py-3 text-left font-semibold">Detail</th>
              </tr>
            </thead>
            <tbody>
              {SYNC_LOG.map((log, i) => (
                <tr key={i} className="border-b hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(log.time)} {fmtTime(log.time)}
                  </td>
                  <td className="px-4 py-2.5 text-xs font-medium">{log.source}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${log.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                      {log.type === 'success' ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      {log.event}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{log.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Generated by Access Cash Forecaster · {reportDate} · Showing last 48 hours of sync activity · For system administrator use
      </p>
    </div>
  );
};
